import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import './AmountInput.css';

interface AmountInputProps {
  value: number;
  onChange: (value: number) => void;
  onNext: () => void;
  onBack: () => void;
  currency?: string;
}

// Символы валют
const currencySymbols: Record<string, string> = {
  'RUB': '₽',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'CNY': '¥',
  'JPY': '¥',
  'INR': '₹',
  'KRW': '₩',
  'TRY': '₺',
};

export const AmountInput: React.FC<AmountInputProps> = ({ value, onChange, onNext, onBack, currency = 'RUB' }) => {
  const { t } = useTranslation();
  const [display, setDisplay] = useState(value > 0 ? value.toString() : '0');
  const currencySymbol = currencySymbols[currency] || currency;

  const handleNumberClick = (num: string) => {
    if (display === '0') {
      setDisplay(num);
    } else if (display.length < 10) {
      setDisplay(display + num);
    }
  };

  const handleDot = () => {
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const handleClear = () => {
    setDisplay('0');
  };

  const handleBackspace = () => {
    if (display.length === 1) {
      setDisplay('0');
    } else {
      setDisplay(display.slice(0, -1));
    }
  };

  const handleSubmit = () => {
    const amount = parseFloat(display);
    if (isNaN(amount) || amount <= 0) {
      return; // Не позволяем отправить некорректную сумму
    }
    onChange(amount);
    onNext();
  };

  const quickAmounts = [100, 500, 1000, 5000];

  const handleQuickAmount = (amount: number) => {
    setDisplay(amount.toString());
  };

  return (
    <div className="amount-input step-content">
      <div className="amount-input__header">
        <button className="amount-input__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="amount-input__header-content">
          <h2 className="amount-input__title">{t('transaction.amount')}</h2>
        </div>
      </div>

      <div className="amount-input__display neo-inset">
        <div className="amount-input__display-value">{display}</div>
        <div className="amount-input__display-currency">{currencySymbol}</div>
      </div>

      <div className="amount-input__quick">
        <div className="amount-input__quick-title">{t('transaction.flow.quickSelect')}:</div>
        <div className="amount-input__quick-buttons">
          {quickAmounts.map((amount) => (
            <button
              key={amount}
              className="quick-amount-btn neo-raised"
              onClick={() => handleQuickAmount(amount)}
            >
              {amount}
            </button>
          ))}
        </div>
      </div>

      <div className="amount-input__keypad">
        <div className="keypad-row">
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('1')}>1</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('2')}>2</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('3')}>3</button>
        </div>
        <div className="keypad-row">
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('4')}>4</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('5')}>5</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('6')}>6</button>
        </div>
        <div className="keypad-row">
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('7')}>7</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('8')}>8</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('9')}>9</button>
        </div>
        <div className="keypad-row">
          <button className="keypad-btn neo-raised" onClick={handleDot}>.</button>
          <button className="keypad-btn neo-raised" onClick={() => handleNumberClick('0')}>0</button>
          <button className="keypad-btn neo-raised keypad-btn--action" onClick={handleBackspace}>⌫</button>
        </div>
        <div className="keypad-row">
          <button className="keypad-btn neo-raised keypad-btn--clear" onClick={handleClear}>{t('transaction.flow.clear')}</button>
          <button
            className="keypad-btn neo-raised keypad-btn--submit"
            onClick={handleSubmit}
            disabled={parseFloat(display) <= 0 || isNaN(parseFloat(display))}
          >
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

